/**
 * @file device-service.ts
 * A client-side service for interacting with the Device Management API.
 */

// --- TYPE DEFINITIONS ---
export interface Device {
  id: string;
  deviceCode: string;
  userCode: string;
  clientId?: string;
  scope?: string;
  status: 'pending' | 'approved' | 'denied';
  createdAt: string;
  expiresAt: string;
  lastPolledAt?: string;
}

// --- CONFIGURATION ---
const API_BASE_URL = '/api';

// --- PRIVATE HELPERS ---

/**
 * Creates and returns authenticated headers for API requests.
 * Note: Better Auth handles authentication automatically via cookies.
 */
function getAuthHeaders(contentType: 'json' | 'none' = 'json'): Headers {
    const headers = new Headers();
    // Better Auth uses HTTP-only cookies for authentication, so no manual token needed
    if (contentType === 'json') {
        headers.append('Content-Type', 'application/json');
    }
    // Include credentials to send cookies with requests
    return headers;
}

/**
 * A generic response handler for fetch calls.
 */
async function handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'An unknown error occurred' }));
        const errorMessage = errorData.error || `Request failed with status ${response.status}`;
        throw new Error(errorMessage);
    }
    return response.json();
}

// --- PUBLIC DEVICE SERVICE ---
export const deviceService = {
    /**
     * Retrieves a list of devices associated with the current user.
     */
    listDevices: async (): Promise<{ devices: Device[] }> => {
        const response = await fetch(`${API_BASE_URL}/devices`, {
            credentials: 'include'
        });
        return handleResponse<{ devices: Device[] }>(response);
    },

    /**
     * Revokes access for a specific device by ID.
     */
    revokeDevice: async (deviceId: string): Promise<{ message: string }> => {
        const headers = getAuthHeaders('none'); // No JSON content type for DELETE
        const response = await fetch(`${API_BASE_URL}/devices/${deviceId}`, {
            method: 'DELETE',
            headers,
            credentials: 'include'
        });
        return handleResponse<{ message: string }>(response);
    },
};